/**
 * Utilitaires pour gérer les emails (compatibilité string/array)
 */

/**
 * Obtient le premier email d'un champ (compatible string et array)
 * @param {string|Array<string>} emails - Email ou tableau d'emails
 * @returns {string|null} - Le premier email ou null
 */
export const getFirstEmail = (emails) => {
  if (!emails) return null;
  if (typeof emails === 'string') return emails;
  if (Array.isArray(emails) && emails.length > 0) return emails[0];
  return null;
};

/**
 * Obtient tous les emails (toujours retourne un array)
 * @param {string|Array<string>} emails - Email ou tableau d'emails
 * @returns {Array<string>} - Tableau d'emails
 */
export const getAllEmails = (emails) => {
  if (!emails) return [];
  if (typeof emails === 'string') return emails.trim() ? [emails] : [];
  if (Array.isArray(emails)) return emails.filter(e => e && e.trim());
  return [];
};

/**
 * Vérifie si un individu a au moins un email
 * @param {Object} individual - L'objet individual
 * @returns {boolean}
 */
export const hasAnyEmail = (individual) => {
  const profEmails = getAllEmails(individual?.professionalEmail);
  const persEmails = getAllEmails(individual?.personalEmail);
  return profEmails.length > 0 || persEmails.length > 0;
};

/**
 * Obtient le meilleur email disponible (pro en priorité)
 * @param {Object} individual - L'objet individual
 * @returns {string|null}
 */
export const getBestEmail = (individual) => {
  return getFirstEmail(individual?.professionalEmail) ||
         getFirstEmail(individual?.personalEmail);
};

/**
 * Normalise un champ email pour l'envoi au backend
 * @param {string|Array<string>} emails - Email(s) à normaliser
 * @returns {Array<string>} - Tableau d'emails nettoyés
 */
export const normalizeEmailsForBackend = (emails) => {
  if (!emails) return [];

  // Si c'est une string, la convertir en array
  if (typeof emails === 'string') {
    return emails.trim() ? [emails.trim()] : [];
  }

  // Si c'est déjà un array, le nettoyer
  if (Array.isArray(emails)) {
    return emails
      .filter(e => e && typeof e === 'string' && e.trim())
      .map(e => e.trim());
  }

  return [];
};

/**
 * Formate les emails pour affichage (avec séparateur)
 * @param {string|Array<string>} emails - Email(s)
 * @param {string} separator - Séparateur (par défaut ', ')
 * @returns {string} - Emails formatés
 */
export const formatEmailsForDisplay = (emails, separator = ', ') => {
  const allEmails = getAllEmails(emails);
  return allEmails.join(separator);
};
